import { Component, signal, computed, inject, output, OnInit, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';

import { take } from 'rxjs';

import { ButtonModule } from '@openng/optimus-ui/button';
import { Dialog } from '@openng/optimus-ui/dialog';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from '@openng/optimus-ui/tabs';
import { Select } from '@openng/optimus-ui/select';
import { SelectButton } from '@openng/optimus-ui/selectbutton';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import JSZip from 'jszip';

import { FileUploadService } from 'src/app/services/file-upload.service';
import { LaunchdarklyService } from 'src/app/services/launchdarkly.service';
import { TreoReferenceDataService } from 'src/app/services/api/treo-reference-data.service';
import { Sop } from 'src/app/models/sop';
import { TestPeriod } from 'src/app/models/test-period';
import { UfNode, UcNode, ScnNode } from 'src/app/models/uf-uc-scn-tree';
import { parseJiraTickets, JIRA_TICKET_HINT } from 'src/app/models/jira.utils';
import { ManualRow } from 'src/app/models/import-dialog.model';

/**
 * Tabbed import dialog for the TREO manual import. It only collects/assembles files; it does
 * not upload — on import it emits the final File to the parent, which runs it through the upload
 * flow.
 *
 * Tabs:
 *   - "Vehicle": two sub-modes —
 *       • Upload: an Excel results slot (.xlsx/.csv) + optional ECU readout (.txt, XCOM).
 *       • Manual: a SOP-driven card list (UF/UC/SCN dropdowns from the abstraction layer) typed
 *         by hand, converted to the same CSV the xlsx import produces; + optional ECU readout.
 *     When a readout is present the results (xlsx or generated CSV) and readout are bundled into
 *     one zip so the backend creates the suites and attaches the readout to each.
 *   - "ILAB" (RESI import): a res.json result file plus environment, suite, and sub-suite typed
 *     by hand, with optional capture (.json), jsondiff (.jsondiff), and ECU readout files. The
 *     res.json is wrapped in a { "res": … } block with environment/suite_name/sub_suite_name
 *     injected (the shape the backend JSON parser expects) and zipped with the extra files.
 */
// Accepted file extensions per slot (lowercase, with the dot). Used to validate dropped files
// the same way the file input's `accept` attribute validates the browse dialog.
const VEHICLE_RESULTS_EXTS = ['.xlsx', '.csv'];
const VEHICLE_READOUT_EXTS = ['.txt'];
const RAW_IMPORT_EXTS = ['.zip', '.json'];
const RESI_RESULT_EXTS = ['.json'];
const RESI_JSONDIFF_EXTS = ['.jsondiff'];
const RESI_ECUID_EXTS = ['.json', '.txt'];

// ILAB RESI environments.
type ResiEnvironment = 'ILAB3' | 'ILAB4';

// Suffix the backend zip handler uses to identify the JSON import file inside a zip.
const TREO_MERGED_RESULTS_SUFFIX = '_merged_results.json';

// LaunchDarkly flag gating the "Raw (zip / json)" manual-import tab.
const MANUAL_ZIP_IMPORT_FLAG = 'manual-zip-import-for-ilab';

@Component({
	selector: 'app-import-dialog',
	standalone: true,
	imports: [FormsModule, ButtonModule, Dialog, Tabs, TabList, Tab, TabPanels, TabPanel, Select, SelectButton, InputTextModule],
	templateUrl: './import-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrl: './import-dialog.component.scss',
})
export class ImportDialogComponent implements OnInit {
	private destroyRef = inject(DestroyRef);

	private fileUploadService = inject(FileUploadService);

	private treoReferenceDataService = inject(TreoReferenceDataService);

	private launchdarklyService = inject(LaunchdarklyService);

	/** Whether the "Raw (zip / json)" manual-import tab is enabled (LaunchDarkly flag). */
	rawImportEnabled = signal<boolean>(false);

	/* =========================================================================================
	 * Shared — outputs, dialog state, lifecycle, and members common to both sub-modes.
	 * ========================================================================================= */

	/** Emits the final file to import (a bundled zip, the Excel results, the generated CSV, or json/zip). */
	readonly importFile = output<File>();

	/** Emits several files to import at once (RESI suite mode: one zip per res.json). */
	readonly importFiles = output<File[]>();

	/** Emits a user-facing error message (rejected drop or failed assembly). */
	readonly importError = output<string>();

	visible = signal<boolean>(false);

	busy = signal<boolean>(false);

	// Which top-level tab is shown: Vehicle (results), ILAB (RESI), or Raw (zip/json passthrough).
	activeTab = signal<'vehicle' | 'ilab' | 'raw'>('vehicle');

	// Vehicle-tab sub-mode: upload a file or fill the manual card list.
	resultsMode = signal<'upload' | 'manual'>('upload');

	readonly resultsModeOptions = [
		{ label: 'Upload file', value: 'upload' },
		{ label: 'Manual input', value: 'manual' },
	];

	// ECU readout (.txt) — shared by both sub-modes; bundled with the results when present.
	selectedReadout = signal<File | null>(null);

	dragOverReadout = signal<boolean>(false);

	readonly jiraTicketHint = JIRA_TICKET_HINT;

	ngOnInit(): void {
		this.launchdarklyService.launchDarklyFlags$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(flags => {
			this.rawImportEnabled.set(!!flags[MANUAL_ZIP_IMPORT_FLAG]);
		});
		this.treoReferenceDataService
			.getSops$()
			.pipe(take(1))
			.subscribe(sops => {
				this.sops.set(sops);
				this.applyDefaultSelections();
			});
		this.treoReferenceDataService
			.getTestPeriods$()
			.pipe(take(1))
			.subscribe(tw => {
				this.testWeeks.set(tw);
				this.applyDefaultSelections();
			});
		// The UF/UC/SCN tree is not SOP-scoped (configuratorUserFunctions returns all active items),
		// so load it once up front rather than per SOP selection.
		this.loadingTree.set(true);
		this.treoReferenceDataService
			.getUfTree$()
			.pipe(take(1))
			.subscribe(tree => {
				this.ufTree.set(tree);
				this.loadingTree.set(false);
			});
	}

	public open(): void {
		this.reset();
		this.applyDefaultSelections();
		this.visible.set(true);
	}

	public close(): void {
		this.visible.set(false);
		this.reset();
	}

	private reset(): void {
		this.activeTab.set('vehicle');
		this.resultsMode.set('upload');
		this.selectedRawFile.set(null);
		this.selectedResults.set(null);
		this.selectedReadout.set(null);
		this.busy.set(false);
		this.selectedSop.set(null);
		this.selectedTestWeek.set(null);
		this.configuration.set('');
		this.rows.set([this.emptyRow()]);
		this.expandedRow.set(0);
		this.resiEnvironment.set('ILAB3');
		this.resiSop.set(null);
		this.resiSuite.set('');
		this.resiSubSuite.set('');
		this.selectedResiResults.set([]);
		this.selectedResiJsonDiff.set(null);
		this.selectedResiEcuid.set(null);
		// Note: ufTree is loaded once in ngOnInit (not SOP-scoped), so it is intentionally not reset.
	}

	public onReadoutSelected(event: Event): void {
		this.selectedReadout.set(this.firstFile(event));
	}

	public onReadoutDrop(event: DragEvent): void {
		this.dragOverReadout.set(false);
		const file = this.acceptDropped(event, VEHICLE_READOUT_EXTS);
		if (file) this.selectedReadout.set(file);
	}

	public clearReadout(): void {
		this.selectedReadout.set(null);
	}

	/**
	 * Bundles the results file (xlsx or generated CSV) and the readout into one zip for import.
	 * `resultsEntryName` is the name the results file gets inside the zip — already carrying the
	 * TREO "__u/__t/__s" markers — so the backend derives the same StartTime/suite_name/SOP as a
	 * direct upload. The zip file name reuses that stem.
	 * @param results
	 * @param readout
	 * @param resultsEntryName
	 */
	private async bundle(results: File, readout: File, resultsEntryName: string): Promise<File> {
		const zip = new JSZip();
		zip.file(resultsEntryName, await results.arrayBuffer());
		zip.file(readout.name, await readout.arrayBuffer());
		const blob = await zip.generateAsync({ type: 'blob' });
		const stem = resultsEntryName.replace(/\.[^.]+$/, '');
		return new File([blob], `${stem}.zip`, { type: 'application/zip' });
	}

	// --- Shared drop/picker helpers ---

	/**
	 * Returns the dropped file if its extension is one of `accepted`; otherwise emits a
	 * rejection message and returns null (so the slot keeps any previously selected file).
	 * @param event
	 * @param accepted
	 */
	private acceptDropped(event: DragEvent, accepted: string[]): File | null {
		const file = this.firstDroppedFile(event);
		if (!file) {
			return null;
		}
		const dot = file.name.lastIndexOf('.');
		const ext = dot >= 0 ? file.name.slice(dot).toLowerCase() : '';
		if (!accepted.includes(ext)) {
			this.importError.emit(`Only ${accepted.join(' / ')} files are accepted here. Rejected: ${file.name}`);
			return null;
		}
		return file;
	}

	private firstFile(event: Event): File | null {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0] ?? null;
		input.value = '';
		return file;
	}

	private firstDroppedFile(event: DragEvent): File | null {
		event.preventDefault();
		event.stopPropagation();
		return event.dataTransfer?.files?.[0] ?? null;
	}

	public allowDrop(event: DragEvent): void {
		event.preventDefault();
		event.stopPropagation();
	}

	/* =========================================================================================
	 * Raw passthrough — drop a full .zip (json + readout/jsondiff/ecuid) or a bare .json and
	 * import it as-is (the original direct-upload behavior, kept for testing).
	 * ========================================================================================= */

	selectedRawFile = signal<File | null>(null);

	dragOverRaw = signal<boolean>(false);

	canImportRaw = computed<boolean>(() => !this.busy() && !!this.selectedRawFile());

	public onRawSelected(event: Event): void {
		this.selectedRawFile.set(this.firstFile(event));
	}

	public onRawDrop(event: DragEvent): void {
		this.dragOverRaw.set(false);
		const file = this.acceptDropped(event, RAW_IMPORT_EXTS);
		if (file) this.selectedRawFile.set(file);
	}

	public clearRaw(): void {
		this.selectedRawFile.set(null);
	}

	/** Emits the raw file unchanged through the normal upload engine. */
	public submitRaw(): void {
		const file = this.selectedRawFile();
		if (!file) return;
		this.importFile.emit(file);
		this.close();
	}

	/* =========================================================================================
	 * Vehicle / Upload sub-mode — the Excel results slot on the "Vehicle" tab.
	 * ========================================================================================= */

	selectedResults = signal<File | null>(null); // Excel/CSV

	dragOverResults = signal<boolean>(false);

	readonly uploadTestWeekHint = 'The file name must begin with a test week, e.g. "Tw2633_...". Please rename the file and try again.';

	/**
	 * True when a file is selected in upload mode but its name doesn't begin with a valid "TwXXXX_"
	 * test-week prefix (the backend's source of truth). Drives the inline hint and blocks import.
	 * The backend's rule is that the part before the first underscore matches /^tw\d{4}$/i.
	 */
	uploadTestWeekInvalid = computed<boolean>(() => {
		const file = this.selectedResults();
		return this.resultsMode() === 'upload' && !!file && !this.hasValidTestWeekPrefix(file.name);
	});

	/**
	 * True when the Vehicle tab's Import button should be enabled. Upload mode needs a file, a SOP
	 * (defaults on open), and a valid "TwXXXX_" prefix on the file name (the test week source).
	 */
	canImportResults = computed<boolean>(
		() => !this.busy() && (this.resultsMode() === 'manual' ? this.manualValid() : !!this.selectedResults() && !!this.selectedSop() && !this.uploadTestWeekInvalid()),
	);

	public onResultsSelected(event: Event): void {
		this.selectedResults.set(this.firstFile(event));
	}

	// Unlike the browse picker (whose `accept` attribute filters by extension), a drop accepts
	// any file, so each slot validates the dropped file against its accepted extensions.
	public onResultsDrop(event: DragEvent): void {
		this.dragOverResults.set(false);
		const file = this.acceptDropped(event, VEHICLE_RESULTS_EXTS);
		if (file) this.selectedResults.set(file);
	}

	public clearResults(): void {
		this.selectedResults.set(null);
	}

	/**
	 * True when the file name begins with a valid test-week prefix ("TwXXXX_", case-insensitive,
	 * exactly four digits), mirroring the backend's treoTestPeriodPattern check on the segment
	 * before the first underscore. Any path prefix (e.g. "xlsx/") is ignored.
	 * @param fileName
	 */
	private hasValidTestWeekPrefix(fileName: string): boolean {
		const base = fileName.split(/[/\\]/).pop() ?? fileName;
		return /^tw\d{4}_/i.test(base);
	}

	/**
	 * Returns a file name's extension including the dot (e.g. ".xlsx"), or "" when it has none.
	 * @param fileName
	 */
	private extOf(fileName: string): string {
		const dot = fileName.lastIndexOf('.');
		return dot >= 0 ? fileName.slice(dot) : '';
	}

	/**
	 * Strips characters that would break the "__s=<sop>" file-name marker ("_" delimits markers).
	 * @param value
	 */
	private markerSafe(value: string): string {
		return value.replace(/[^A-Za-z0-9]/g, '');
	}

	/**
	 * Submits the Vehicle tab. In upload mode the file name's "TwXXXX_" prefix is the test-week
	 * source of truth (validated before submit), so the file is emitted as-is apart from a "__s="
	 * SOP marker (bundled with the readout when present); in manual mode it generates a CSV from the
	 * entries (same columns the xlsx import produces) and emits that (bundled with the readout).
	 *
	 * Zipped (readout present) and un-zipped uploads differ in where the "__u=/__t=" markers come
	 * from: an un-zipped file is re-marked by FileUploadService on upload, so here we only add the
	 * SOP marker; a zipped file bypasses that path, so the zip entry name is fully marked here
	 * (SOP + user/timestamp).
	 */
	public async submitResults(): Promise<void> {
		if (this.busy()) {
			return;
		}
		this.busy.set(true);
		try {
			const readout = this.selectedReadout();

			if (this.resultsMode() === 'upload') {
				const picked = this.selectedResults();
				if (!picked) {
					this.busy.set(false);
					return;
				}
				// Keep the original name (its TwXXXX_ prefix is the test week); only add the SOP marker.
				const sop = this.selectedSop()?.name;
				const dot = picked.name.lastIndexOf('.');
				const stem = dot >= 0 ? picked.name.slice(0, dot) : picked.name;
				const ext = this.extOf(picked.name);
				// Un-zipped: SOP marker only; FileUploadService adds __u/__t on upload. Zipped: the service
				// is bypassed, so the zip entry name is fully marked here.
				const sopMarker = sop ? `__s=${this.markerSafe(sop)}` : '';
				const results = new File([picked], `${stem}${sopMarker}${ext}`, { type: picked.type, lastModified: picked.lastModified });
				const file = readout ? await this.bundle(results, readout, this.fileUploadService.treoMarkedFileName(picked.name, sop)) : results;
				this.importFile.emit(file);
				this.close();
				return;
			}

			const results = this.buildManualCsvFile();
			// Manual CSV is already fully marked by buildManualCsvFile; the zip entry reuses that name.
			const file = readout ? await this.bundle(results, readout, results.name) : results;
			this.importFile.emit(file);
			this.close();
		} catch (err) {
			this.busy.set(false);
			console.error('[ImportDialog] Failed to prepare files for import:', err);
			this.importError.emit('Failed to prepare the files for import. Please try again.');
		}
	}

	/* =========================================================================================
	 * ILAB / RESI sub-mode — one or more res.json plus environment/SOP/suite/sub-suite and an
	 * optional jsondiff (single result only) and ECU readout, each wrapped + zipped into the shape
	 * the backend JSON parser understands. Covers TREO's single "RESI Import" and bulk "RESI Import
	 * Suite" with one view: a single file is just a list of length one.
	 * ========================================================================================= */

	resiEnvironment = signal<ResiEnvironment>('ILAB3');

	readonly resiEnvironmentOptions: ResiEnvironment[] = ['ILAB3', 'ILAB4'];

	resiSop = signal<Sop | null>(null);

	resiSuite = signal<string>('');

	resiSubSuite = signal<string>('');

	selectedResiResults = signal<File[]>([]); // one or more res.json

	selectedResiJsonDiff = signal<File | null>(null); // capture .jsondiff (single result only)

	selectedResiEcuid = signal<File | null>(null); // ecuid.json or XCOM .txt readout (shared)

	dragOverResiResults = signal<boolean>(false);

	dragOverResiJsonDiff = signal<boolean>(false);

	dragOverResiEcuid = signal<boolean>(false);

	/**
	 * True unless multiple results are selected (0 or 1 file) — gates the single-result-only fields
	 * (sub-suite, jsondiff), which don't generalize to a batch.
	 */
	resiSingleResult = computed<boolean>(() => this.selectedResiResults().length <= 1);

	/** RESI import needs the environment, a suite, and at least one res.json. */
	canImportResi = computed<boolean>(() => !this.busy() && !!this.resiEnvironment() && !!this.resiSuite().trim() && this.selectedResiResults().length > 0);

	public onResiJsonDiffSelected(event: Event): void {
		this.selectedResiJsonDiff.set(this.firstFile(event));
	}

	public onResiEcuidSelected(event: Event): void {
		this.selectedResiEcuid.set(this.firstFile(event));
	}

	public onResiJsonDiffDrop(event: DragEvent): void {
		this.dragOverResiJsonDiff.set(false);
		const file = this.acceptDropped(event, RESI_JSONDIFF_EXTS);
		if (file) this.selectedResiJsonDiff.set(file);
	}

	public onResiEcuidDrop(event: DragEvent): void {
		this.dragOverResiEcuid.set(false);
		const file = this.acceptDropped(event, RESI_ECUID_EXTS);
		if (file) this.selectedResiEcuid.set(file);
	}

	public clearResiJsonDiff(): void {
		this.selectedResiJsonDiff.set(null);
	}

	public clearResiEcuid(): void {
		this.selectedResiEcuid.set(null);
	}

	// Result files: one or more res.json, appended to a list (deduped by name), removable individually.
	public onResiResultsSelected(event: Event): void {
		const input = event.target as HTMLInputElement;
		this.appendResiResults(Array.from(input.files ?? []));
		input.value = '';
	}

	public onResiResultsDrop(event: DragEvent): void {
		this.dragOverResiResults.set(false);
		event.preventDefault();
		event.stopPropagation();
		const dropped = Array.from(event.dataTransfer?.files ?? []);
		const accepted: File[] = [];
		for (const file of dropped) {
			const dot = file.name.lastIndexOf('.');
			const ext = dot >= 0 ? file.name.slice(dot).toLowerCase() : '';
			if (RESI_RESULT_EXTS.includes(ext)) {
				accepted.push(file);
			} else {
				this.importError.emit(`Only ${RESI_RESULT_EXTS.join(' / ')} files are accepted here. Rejected: ${file.name}`);
			}
		}
		this.appendResiResults(accepted);
	}

	private appendResiResults(files: File[]): void {
		if (files.length === 0) {
			return;
		}
		// No dedupe by name: every result is "res.json", so they collide on name but are distinct
		// files (they differ by content/source folder). Just append; the X button removes by index.
		this.selectedResiResults.update(existing => [...existing, ...files]);
		// A jsondiff and an explicit sub-suite only make sense for a single result; clear both once a
		// second file is added (with multiple results each falls back to its own configuration).
		if (this.selectedResiResults().length > 1) {
			this.selectedResiJsonDiff.set(null);
			this.resiSubSuite.set('');
		}
	}

	public removeResiResult(index: number): void {
		this.selectedResiResults.update(files => files.filter((_, i) => i !== index));
	}

	/**
	 * Opens the dialog on the ILAB/RESI tab with the suite, sub-suite, SOP, and environment
	 * prefilled (editable), used by the "Manual RESI import" button in the suite-details header.
	 * The SOP defaults to "Main" (the latest) unless a code is supplied that matches a loaded SOP.
	 * @param prefill
	 * @param prefill.suite
	 * @param prefill.subSuite
	 * @param prefill.environment
	 * @param prefill.sop
	 */
	public openResi(prefill?: { suite?: string; subSuite?: string; environment?: ResiEnvironment; sop?: string }): void {
		this.reset();
		this.activeTab.set('ilab');
		if (prefill?.suite) this.resiSuite.set(prefill.suite);
		if (prefill?.subSuite) this.resiSubSuite.set(prefill.subSuite);
		if (prefill?.environment) this.resiEnvironment.set(prefill.environment);
		this.applyDefaultResiSop(prefill?.sop);
		this.visible.set(true);
	}

	/**
	 * Preselects the RESI SOP: the one whose code matches `sopCode` if given and present, otherwise
	 * "Main" (the first SOP, the latest). No-op if the SOP list hasn't loaded yet.
	 * @param sopCode
	 */
	private applyDefaultResiSop(sopCode?: string): void {
		const list = this.sops();
		if (list.length === 0) {
			return;
		}
		const match = sopCode ? list.find(s => String(s.sopNumber) === sopCode || s.name === sopCode) : undefined;
		this.resiSop.set(match ?? list[0]);
	}

	/**
	 * Submits the ILAB/RESI tab. Each res.json is parsed, has the chosen environment/suite/sub-suite/
	 * SOP injected, is wrapped in a { "res": … } block (the shape ParseJSONFromBytesTREO
	 * expects), then zipped as "<stem>_merged_results.json" so the backend treats it as the import
	 * file — each becoming its own main suite tagged with the shared suite_name. The readout (if any)
	 * is copied into every zip; a jsondiff is only attached when there's a single result. Emits all
	 * zips at once through the normal upload flow.
	 */
	public async submitResi(): Promise<void> {
		if (this.busy() || !this.canImportResi()) {
			return;
		}
		this.busy.set(true);
		try {
			const results = this.selectedResiResults();
			const jsonDiff = results.length === 1 ? this.selectedResiJsonDiff() : null;
			const files: File[] = [];
			for (let i = 0; i < results.length; i++) {
				const wrapped = await this.wrapResiResult(results[i]);
				if (!wrapped) {
					return; // parse error already surfaced; abort the whole batch
				}
				// The files are all named "res.json", so name each zip after the result's testscript_name
				// (unique per TC) with an index fallback, to keep zips and their entries distinct.
				files.push(await this.bundleResi(wrapped.json, wrapped.testscriptName || `result_${i + 1}`, [jsonDiff, this.selectedResiEcuid()]));
			}
			this.importFiles.emit(files);
			this.close();
		} catch (err) {
			this.busy.set(false);
			console.error('[ImportDialog] Failed to prepare RESI files for import:', err);
			this.importError.emit('Failed to prepare the RESI files for import. Please try again.');
		}
	}

	/**
	 * Parses one res.json and injects the manually entered fields, returning the wrapped
	 * { "res": … } JSON string and the result's testscript_name (used to name the zip, since every
	 * file is "res.json"). SOP is sent as its numeric code (e.g. "2610"); for "Main" that is the
	 * latest SOP's number, matching res.sop's format. Returns null (and surfaces an error, clearing
	 * busy) when the file isn't valid JSON.
	 * @param resultFile
	 */
	private async wrapResiResult(resultFile: File): Promise<{ json: string; testscriptName: string } | null> {
		let res: Record<string, unknown>;
		try {
			res = JSON.parse(await resultFile.text());
		} catch {
			this.busy.set(false);
			this.importError.emit(`Could not parse ${resultFile.name} as JSON. Please choose a valid res.json file.`);
			return null;
		}
		const testscriptName = typeof res['testscript_name'] === 'string' ? (res['testscript_name'] as string) : '';
		res['environment'] = this.resiEnvironment();
		res['suite_name'] = this.resiSuite().trim();
		res['sub_suite_name'] = this.resiSubSuite().trim();
		const sop = this.resiSop();
		if (sop) {
			res['sop'] = String(sop.sopNumber);
		}
		return { json: JSON.stringify({ res }), testscriptName };
	}

	/**
	 * Builds a RESI import zip: the wrapped JSON named "<stem>_merged_results.json" (so the zip
	 * handler picks it up as the import file) plus the given attachments (jsondiff/readout; nulls
	 * skipped). Extensions are kept so the backend tags jsondiff/json/txt correctly. The stem
	 * combines the suite name and a per-result label (the testscript_name) so each zip and its
	 * import entry stay distinct even though every source file is named "res.json".
	 * @param wrappedJson
	 * @param resultLabel
	 * @param attachments
	 */
	private async bundleResi(wrappedJson: string, resultLabel: string, attachments: (File | null)[]): Promise<File> {
		const zip = new JSZip();
		const suiteStem =
			this.resiSuite()
				.trim()
				.replace(/[^A-Za-z0-9_-]/g, '_') || 'resi';
		const resultStem = resultLabel.replace(/\.[^.]+$/, '').replace(/[^A-Za-z0-9_-]/g, '_');
		const stem = `${suiteStem}_${resultStem}`;
		zip.file(`${stem}${TREO_MERGED_RESULTS_SUFFIX}`, wrappedJson);

		for (const f of attachments) {
			if (f) {
				zip.file(f.name, await f.arrayBuffer());
			}
		}

		const blob = await zip.generateAsync({ type: 'blob' });
		return new File([blob], `${stem}_resi.zip`, { type: 'application/zip' });
	}

	/* =========================================================================================
	 * Manual sub-mode — SOP/test-week/config + the editable UF/UC/SCN card list.
	 * ========================================================================================= */

	sops = signal<Sop[]>([]);

	selectedSop = signal<Sop | null>(null);

	testWeeks = signal<TestPeriod[]>([]);

	selectedTestWeek = signal<TestPeriod | null>(null);

	ufTree = signal<UfNode[]>([]); // active UF→UC→SCN tree (not SOP-scoped)

	loadingTree = signal<boolean>(false);

	configuration = signal<string>(''); // vehicle configuration name

	rows = signal<ManualRow[]>([this.emptyRow()]);

	readonly resultOptions = ['OK', 'NOK'];

	// Accordion state: index of the single expanded entry (others collapse to a summary line). The
	// newest entry stays expanded; older ones can be clicked open to edit.
	expandedRow = signal<number>(0);

	// Manual import is valid when test week, SOP, config are set, at least one full row exists,
	// and no row's EV field contains an invalid Jira ticket.
	manualValid = computed<boolean>(() => {
		if (!this.selectedTestWeek() || !this.selectedSop() || !this.configuration().trim()) {
			return false;
		}
		if (this.rows().some(r => parseJiraTickets(r.ev).invalid.length > 0)) {
			return false;
		}
		return this.rows().some(r => r.uf && r.scn);
	});

	/** True when any row's EV field has an invalid Jira ticket (drives the inline hint). */
	hasInvalidEv = computed<boolean>(() => this.rows().some(r => parseJiraTickets(r.ev).invalid.length > 0));

	/**
	 * Preselects "Main" (the first SOP) and the current test week. The current test week is the one
	 * whose validity window contains now (validFrom ≤ now ≤ validTo), matching ECU Compare; if none
	 * matches, falls back to the newest (the first, since getTestPeriods returns newest first). Only
	 * sets a value when the list is loaded and nothing is selected yet, so it's safe to call from
	 * both load and open.
	 */
	private applyDefaultSelections(): void {
		if (!this.selectedSop() && this.sops().length > 0) {
			this.selectedSop.set(this.sops()[0]);
		}
		// Default the RESI SOP to "Main" too, so it is preselected whether the ILAB tab is reached via
		// openResi or by switching tabs after a plain open(). (openResi may override with a matched code.)
		if (!this.resiSop() && this.sops().length > 0) {
			this.resiSop.set(this.sops()[0]);
		}
		if (!this.selectedTestWeek() && this.testWeeks().length > 0) {
			const now = Date.now();
			const current = this.testWeeks().find(tw => {
				const from = tw.validFrom?.getTime();
				const to = tw.validTo?.getTime();
				return from != null && to != null && from <= now && now <= to;
			});
			this.selectedTestWeek.set(current ?? this.testWeeks()[0]);
		}
	}

	public onSopChange(sop: Sop | null): void {
		// SOP is metadata only now — the UF/UC/SCN tree is loaded once and is not SOP-scoped, so
		// changing SOP doesn't reload the tree or reset the entries.
		this.selectedSop.set(sop);
	}

	/**
	 * UC options for a row depend on its selected UF.
	 * @param row
	 */
	public ucOptions(row: ManualRow): UcNode[] {
		return row.uf?.ucs ?? [];
	}

	/**
	 * SCN options for a row depend on its selected UC (or all of the UF's SCNs if no UC chosen).
	 * @param row
	 */
	public scnOptions(row: ManualRow): ScnNode[] {
		if (row.uc) {
			return row.uc.scns;
		}
		if (row.uf) {
			return row.uf.ucs.flatMap(uc => uc.scns);
		}
		return [];
	}

	public onRowUfChange(index: number, uf: UfNode | null): void {
		this.rows.update(rows => rows.map((r, i) => (i === index ? { ...r, uf, uc: null, scn: null } : r)));
	}

	public onRowUcChange(index: number, uc: UcNode | null): void {
		this.rows.update(rows => rows.map((r, i) => (i === index ? { ...r, uc, scn: null } : r)));
	}

	public setRow(index: number, patch: Partial<ManualRow>): void {
		this.rows.update(rows => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
	}

	public addRow(): void {
		this.rows.update(rows => [...rows, this.emptyRow()]);
		// Collapse the previous entries and expand the new (last) one for editing.
		this.expandedRow.set(this.rows().length - 1);
	}

	/**
	 * Removes an entry. When it's the only entry, it is cleared (reset to empty) instead of removed,
	 * so the list always keeps at least one editable entry.
	 * @param index
	 */
	public removeRow(index: number): void {
		this.rows.update(rows => (rows.length > 1 ? rows.filter((_, i) => i !== index) : [this.emptyRow()]));
		// Keep a valid entry expanded after removal (clamp to the new last index).
		this.expandedRow.set(Math.min(this.expandedRow(), this.rows().length - 1));
	}

	/**
	 * Expands the clicked entry (accordion: opening one collapses the rest).
	 * @param index
	 */
	public toggleRow(index: number): void {
		this.expandedRow.set(this.expandedRow() === index ? -1 : index);
	}

	/**
	 * One-line summary shown on a collapsed entry, e.g. "UF12 ▸ SCN7 — NOK EV-1234".
	 * @param row
	 */
	public rowSummary(row: ManualRow): string {
		if (!row.uf && !row.scn) {
			return 'Empty entry';
		}
		const parts: string[] = [];
		if (row.uf) parts.push(`UF${row.uf.id}`);
		if (row.scn) parts.push(`SCN${row.scn.id}`);
		const left = parts.join(' ▸ ');
		const ev = parseJiraTickets(row.ev).valid.join(' ');
		const right = row.result === 'NOK' ? (ev ? `NOK ${ev}` : 'NOK') : 'OK';
		return left ? `${left} - ${right}` : right;
	}

	private emptyRow(): ManualRow {
		return { uf: null, uc: null, scn: null, ev: '', result: 'OK' };
	}

	/**
	 * Builds a CSV File from the manual entries in the same column shape the xlsx import produces, so
	 * it flows through the existing backend spreadsheet parser (one main suite per UF). EVs are
	 * embedded in the Result text (e.g. "NOK EV-1234"), matching how the parser extracts them. The
	 * file name carries the test-week-less config as the stem; the backend derives config from it.
	 */
	private buildManualCsvFile(): File {
		const header = ['UFNumber', 'UFName', 'SCNNumber', 'SCNName', 'Result'];
		const lines = [header.join(',')];
		for (const r of this.rows()) {
			if (!r.uf || !r.scn) {
				continue; // skip incomplete rows
			}
			// Use only valid, normalized (uppercased) EV tickets; invalid tokens are blocked upstream
			// by manualValid, so the file is never built with them.
			const evs = parseJiraTickets(r.ev).valid.join(' ');
			const resultText = r.result === 'NOK' ? (evs ? `NOK ${evs}` : 'NOK') : 'OK';
			lines.push([this.csv(String(r.uf.id)), this.csv(r.uf.name), this.csv(String(r.scn.id)), this.csv(r.scn.name), this.csv(resultText)].join(','));
		}
		const csv = lines.join('\n');

		// The backend derives test period + config from the file name: "<TwXXXX>_<config>.csv".
		// The marker helper appends the __u=/__t= (and __s=<sop>) markers so StartTime/suite_name/SOP
		// match the xlsx-upload path. (manualValid guarantees a test week and config are set before submit.)
		const testWeek = this.selectedTestWeek()!.name;
		const config = this.configuration().trim();
		const baseName = this.fileUploadService.treoMarkedFileName(`${testWeek}_${config}.csv`, this.selectedSop()?.name);
		return new File([csv], baseName, { type: 'text/csv' });
	}

	/**
	 * Minimal CSV field escaping.
	 * @param value
	 */
	private csv(value: string): string {
		if (/[",\n]/.test(value)) {
			return `"${value.replace(/"/g, '""')}"`;
		}
		return value;
	}
}
